import type { ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

import { SuccessCircledIcon } from '@slite/icons'

import type { BaseLabelProps } from './BaseLabel'
import { BaseLabel } from './BaseLabel'

export type LabelProps = Omit<BaseLabelProps, 'children'> & {
  children?: ReactNode
  'data-test-id'?: string
  ref?: React.Ref<HTMLDivElement>
}

export function Label({
  children,
  'data-test-id': testId,
  ref,
  ...props
}: LabelProps) {
  return (
    <BaseLabel {...props}>
      {(_className, _style, renderContent) => (
        <div
          className={_className}
          style={_style}
          ref={ref}
          data-test-id={testId}
        >
          {renderContent(children)}
        </div>
      )}
    </BaseLabel>
  )
}

/** A state that went well, styled like the Verified state of a doc. */
export function SuccessLabel({
  children,
  'data-test-id': testId,
}: {
  children: ReactNode
  'data-test-id'?: string
}) {
  return (
    <Label
      variant="success"
      uppercase={false}
      icon={SuccessCircledIcon}
      iconClassName="margin-right-4"
      className="font-size-normal font-weight-semibold flexbox-no-shrink"
      data-test-id={testId}
    >
      {children}
    </Label>
  )
}

export const NewLabel = ({ size }: { size?: BaseLabelProps['size'] }) => {
  const intl = useIntl()
  return (
    <Label
      variant="primary"
      size={size ?? 'small'}
      className="margin-left-8 uppercase"
    >
      {intl.formatMessage({
        id: 'new',
        defaultMessage: 'new',
      })}
    </Label>
  )
}

export const BetaLabel = () => {
  const intl = useIntl()
  return (
    <Label variant="warn" size="small" className="margin-left-8 uppercase">
      {intl.formatMessage({
        id: 'beta',
        defaultMessage: 'beta',
      })}
    </Label>
  )
}
